import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Modal, Space, Typography, message, theme } from "antd";
import {
  FileAddOutlined,
  CheckCircleOutlined,
  SendOutlined,
  LikeOutlined,
  DislikeOutlined,
  FileDoneOutlined,
  FundOutlined,
  RiseOutlined,
  DiffOutlined,
  CameraOutlined,
  ExportOutlined
} from "@ant-design/icons";
import { Error, Success } from "../../../commons/Messages/Messages";
import axios from "axios";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import AddOffers from "./AddOffers";
import CreateInvoiceModal from "./CreateInvoiceModal";
import moment from "moment";
import DropdownComp from "../../Dropdowns/DropdownComp";
import GeneralTable from "../../GeneralTable";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import Tagcomp from "../../TagComp/Tagcomp";
import { branchLabel, branchOfDoc } from "../customerBranches";
import { useTranslation } from "react-i18next";
import { downloadOffer } from "../../../utils/downloadOffer";
import OfferBillingPanel, { ConfirmOfferModal } from "./OfferBillingPanel";
import ProgressAdvanceModal from "./ProgressAdvanceModal";
import * as salesApi from "./salesApi";
import SiteDocsPanel from "../../../serviceProvider/components/siteDocs/SiteDocsPanel";
import { canUseSiteDocs } from "../../../serviceProvider/components/siteDocs/access";
import GaebExportModal from "../../../serviceProvider/components/gaeb/GaebExportModal";
import { countingFlags } from "../../../serviceProvider/components/gaeb/calc";
import "./Offers.css";

const CONFIRMABLE = ["Accepted", "Partially Invoiced", "Invoiced"];

const OffersList = ({ data, updateParent, pageNo, pageSize, totalCount, setPageNo }) => {
  const [loading, setLoading] = useState(false);
  const [selectedOfferForInvoice, setSelectedOfferForInvoice] = useState(null);
  const { t } = useTranslation("dashboard");
  const { t: ta } = useTranslation("accounting");
  const history = useHistory();
  const { token } = theme.useToken();
  const [billingOffer, setBillingOffer] = useState(null);
  const [docsOffer, setDocsOffer] = useState(null);
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [progressOffer, setProgressOffer] = useState(null);
  const [gaebOffer, setGaebOffer] = useState(null);

  const deleteHandler = async id => {
    try {
      const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/quotations/${id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      if (res.status === 200) {
        Success(t("Offer deleted successfully"));
        updateParent();
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  // AO-1: sending allocates the gapless offer number; the invoicing statuses are derived
  const changeStatus = async (obj, status) => {
    try {
      const res = await salesApi.setOfferStatus(obj._id, status);
      const number = res.data?.offer?.offerNumber;
      message.success(
        status === "Sent" && number ? `${ta("Offer sent")}: ${number}` : ta("Offer status updated")
      );
      updateParent();
    } catch (err) {
      message.error(salesApi.errorText(err));
    }
  };

  const linkButton = (key, icon, label, onClick) => ({
    key,
    component: () => (
      <Button type="link" style={{ color: "var(--ds-text)" }} onClick={onClick}>
        {icon} {label}
      </Button>
    )
  });

  const downloadPDF = async id => {
    try {
      await downloadOffer(id, setLoading);
    } catch {
      Error(t("Failed to download offer PDF"));
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "quotationNumber",
      sorter: (a, b) =>
        String(a.offerNumber || a.quotationNumber || "").localeCompare(
          String(b.offerNumber || b.quotationNumber || "")
        ),
      render: (text, obj) => (
        <Space direction="vertical" size={0}>
          <span>{obj.offerNumber || text || "—"}</span>
          {obj.offerNumber && text && text !== obj.offerNumber ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {text}
            </Typography.Text>
          ) : null}
          {obj.confirmationNumber ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {ta("Order confirmation")} {obj.confirmationNumber}
            </Typography.Text>
          ) : null}
        </Space>
      )
    },
    {
      title: t("Title"),
      dataIndex: "title",
      mobile: "title",
      sorter: (a, b) => a.title?.localeCompare(b.title)
    },
    {
      title: t("Client"),
      dataIndex: "client",
      render: (text, obj) => (
        <div className="offers-list__client">
          {obj?.client?.firstName} {obj?.client?.lastName}
          {obj?.client?.company && (
            <small className="offers-list__company">{obj.client.company}</small>
          )}
          {branchOfDoc(obj) ? (
            <small className="offers-list__company">{branchLabel(branchOfDoc(obj))}</small>
          ) : null}
        </div>
      )
    },
    {
      title: t("Total Amount"),
      dataIndex: "totalAmount",
      render: (val, obj) => (
        <strong>
          {val?.toFixed(2)}{" "}
          {obj?.currency === "USD" ? "$" : obj?.currency === "EUR" ? "€" : obj?.currency || "€"}
        </strong>
      ),
      sorter: (a, b) => a.totalAmount - b.totalAmount
    },
    {
      title: t("Date"),
      dataIndex: "date",
      render: text => <div>{moment(text).format("DD.MM.YYYY")}</div>
    },
    {
      title: t("Status"),
      dataIndex: "status",
      mobile: "subtitle",
      render: text => <Tagcomp text={text} />
    },
    {
      title: t("Create Invoice"),
      key: "createInvoice",
      mobile: "actions",
      render: (text, obj) => {
        const isDeclined = obj?.status === "Declined";
        const isFullyInvoiced =
          obj?.status === "Invoiced" ||
          (Array.isArray(obj?.items) &&
            obj.items.length > 0 &&
            obj.items.every(
              (i, index) =>
                !countingFlags(obj.items)[index] || (i.invoicedQuantity || 0) >= (i.quantity || 0)
            ));

        if (isFullyInvoiced) {
          return (
            <Button
              icon={<CheckCircleOutlined style={{ color: token.colorSuccess }} />}
              onClick={() => setSelectedOfferForInvoice(obj)}
            >
              {t("Invoiced")}
            </Button>
          );
        }

        return (
          <Button
            type="primary"
            icon={<FileAddOutlined />}
            disabled={isDeclined}
            onClick={() => setSelectedOfferForInvoice(obj)}
          >
            {t("Create Invoice")}
          </Button>
        );
      }
    },
    {
      title: t("Download"),
      key: "download",
      mobile: "actions",
      render: (text, obj) => (
        <Button onClick={() => downloadPDF(obj?._id)}>{t("Print Offer")}</Button>
      )
    },
    {
      title: t("Action"),
      key: "action",
      mobile: "actions",
      render: (text, obj) => {
        const status = obj?.status || "Draft";
        const statusItems = [];
        if (status === "Draft")
          statusItems.push(
            linkButton("send", <SendOutlined />, ta("Mark as sent"), () =>
              changeStatus(obj, "Sent")
            )
          );
        if (["Draft", "Sent", "Declined"].includes(status))
          statusItems.push(
            linkButton("accept", <LikeOutlined />, ta("Mark as accepted"), () =>
              changeStatus(obj, "Accepted")
            )
          );
        if (["Draft", "Sent"].includes(status))
          statusItems.push(
            linkButton("decline", <DislikeOutlined />, ta("Mark as declined"), () =>
              changeStatus(obj, "Declined")
            )
          );
        if (CONFIRMABLE.includes(status))
          statusItems.push(
            linkButton(
              "confirm",
              <FileDoneOutlined />,
              obj.confirmationNumber ? ta("Order confirmation") : ta("Create order confirmation"),
              () => setConfirmOffer(obj)
            )
          );
        // Nachträge to an awarded offer (service provider panel)
        if (CONFIRMABLE.includes(status) && window.location.pathname.startsWith("/user"))
          statusItems.push(
            linkButton("changeOrders", <DiffOutlined />, t("Change orders"), () =>
              history.push(`/user/change-orders?offer=${obj._id}`)
            )
          );
        if (!["Draft", "Declined"].includes(status))
          statusItems.push(
            linkButton("progress", <RiseOutlined />, ta("Abschlag by progress"), () =>
              setProgressOffer(obj)
            )
          );
        statusItems.push(
          linkButton("billing", <FundOutlined />, ta("Billing overview"), () =>
            setBillingOffer(obj)
          )
        );
        // GAEB X84 (Angebotsabgabe) of an imported LV, or X83/X81 of any offer
        if (window.location.pathname.startsWith("/user"))
          statusItems.push(
            linkButton(
              "gaeb",
              <ExportOutlined />,
              obj.gaeb ? t("Export GAEB (X84)") : t("Export GAEB"),
              () => setGaebOffer(obj)
            )
          );
        // Photos and notes from the site assigned to this offer
        if (canUseSiteDocs())
          statusItems.push(
            linkButton("siteDocs", <CameraOutlined />, t("Documentation"), () => setDocsOffer(obj))
          );
        let menuItems = [
          ...statusItems,
          {
            key: "createInvoice",
            component: () => (
              <Button
                type="link"
                style={{ color: "var(--ds-text)" }}
                onClick={() => setSelectedOfferForInvoice(obj)}
              >
                <FileAddOutlined /> {t("Create Invoice")}
              </Button>
            )
          },
          {
            key: "edit",
            component: () => <AddOffers data={obj} isUpdate={true} updateParent={updateParent} />
          },
          {
            key: "delete",
            component: () => (
              <DeleteModal text={t("Offer")} onClick={() => deleteHandler(obj?._id)} />
            )
          }
        ];
        return <DropdownComp menuItems={menuItems} />;
      }
    }
  ];

  return (
    <>
      <GeneralTable
        loading={loading}
        pageNo={pageNo}
        pageSize={pageSize}
        totalCount={totalCount}
        data={data}
        columns={columns}
        setPageNo={setPageNo}
      />

      <Modal
        open={Boolean(docsOffer)}
        onCancel={() => setDocsOffer(null)}
        footer={null}
        width={760}
        destroyOnClose
        title={`${t("Documentation")} · ${docsOffer?.offerNumber || docsOffer?.quotationNumber || ""} ${docsOffer?.title || ""}`}
      >
        {docsOffer ? (
          <SiteDocsPanel
            filter={{ offer: docsOffer._id }}
            client={docsOffer.client?._id || docsOffer.client || ""}
            customerEmail={docsOffer.client?.email || ""}
            reportTitle={docsOffer.title || ""}
          />
        ) : null}
      </Modal>

      <Modal
        open={Boolean(billingOffer)}
        onCancel={() => setBillingOffer(null)}
        footer={null}
        width={960}
        destroyOnClose
        title={`${ta("Billing overview")} · ${billingOffer?.offerNumber || billingOffer?.quotationNumber || ""} ${billingOffer?.title || ""}`}
      >
        {billingOffer ? <OfferBillingPanel offer={billingOffer} onChanged={updateParent} /> : null}
      </Modal>
      <ConfirmOfferModal
        open={Boolean(confirmOffer)}
        offer={confirmOffer}
        onClose={() => setConfirmOffer(null)}
        onDone={updateParent}
      />
      <GaebExportModal
        open={Boolean(gaebOffer)}
        offer={gaebOffer}
        onClose={() => setGaebOffer(null)}
      />
      <ProgressAdvanceModal
        open={Boolean(progressOffer)}
        offer={progressOffer}
        onClose={() => setProgressOffer(null)}
        onCreated={updateParent}
      />

      {selectedOfferForInvoice && (
        <CreateInvoiceModal
          open={Boolean(selectedOfferForInvoice)}
          offer={selectedOfferForInvoice}
          onClose={() => setSelectedOfferForInvoice(null)}
          onSuccess={() => {
            setSelectedOfferForInvoice(null);
            updateParent();
          }}
        />
      )}
    </>
  );
};

export default OffersList;
